import { FONT_FAMILY, PRIMARY_COLOR, WebAppCommonModule } from "@allbesmart/web-app-common";
import {
  AfterViewInit,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
} from "@angular/core";
import * as echarts from "echarts";
import { OrderDto } from "src/app/models/order-dto";
import { v4 as uuidv4 } from "uuid";
import { DatePipe } from "@angular/common";

interface LogEntry {
  id?: string;
  username?: string;
  message?: string;
  timestamp: Date;
}

interface DayBucket {
  key: string;
  title: string;
  date: Date;
  count: number;
}

@Component({
    selector: "oaibox-order-logs-modal",
    templateUrl: "./order-logs-modal.component.html",
    styleUrl: "./order-logs-modal.component.css",
    imports: [WebAppCommonModule, DatePipe],
})
export class OrderLogsModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input({ required: true }) order!: OrderDto;

  protected readonly ALL_LABEL = "All";
  protected readonly timelineChartId = uuidv4();

  protected selectedDateLabel: string = this.ALL_LABEL;
  protected filteredLogs: LogEntry[] = [];
  protected hasLogs: boolean = false;

  private static readonly MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  private logs: LogEntry[] = [];
  private dayBuckets: DayBucket[] = [];
  private selectedDayKey?: string;

  private chart?: echarts.ECharts;
  private readonly resize = (): void => this.chart?.resize();
  private readonly resizeObserver = new ResizeObserver(this.resize);

  constructor(private readonly zone: NgZone) {}

  ngOnInit(): void {
    this.logs = (this.order.logs ?? [])
      .map((entry) => ({
        ...entry,
        timestamp: new Date(entry.timestamp ?? 0),
      }))
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    this.hasLogs = this.logs.length > 0;
    this.dayBuckets = this.buildDayBuckets(this.logs);

    this.applyFilter();
  }

  ngAfterViewInit(): void {
    const element = document.getElementById(this.timelineChartId);
    if (!element) return;

    this.chart = echarts.init(element);
    this.chart.on("click", (params: { dataIndex: number }) =>
      this.onTimelineClick(params.dataIndex)
    );
    this.renderTimeline();
    this.resizeObserver.observe(element);
  }

  private onTimelineClick(dataIndex: number): void {
    const bucket = this.dayBuckets[dataIndex];
    if (!bucket) return;

    this.zone.run(() => {
      // Clicking the selected day again clears the filter back to "All".
      this.selectedDayKey =
        this.selectedDayKey === bucket.key ? undefined : bucket.key;

      this.applyFilter();
      this.renderTimeline();
    });
  }

  private applyFilter(): void {
    if (!this.selectedDayKey) {
      this.selectedDateLabel = this.ALL_LABEL;
      this.filteredLogs = this.logs;
      return;
    }

    const bucket = this.dayBuckets.find((b) => b.key === this.selectedDayKey);
    this.selectedDateLabel = bucket?.title ?? this.ALL_LABEL;
    this.filteredLogs = this.logs.filter(
      (entry) => this.dayKey(entry.timestamp) === this.selectedDayKey
    );
  }

  private buildDayBuckets(logs: LogEntry[]): DayBucket[] {
    const buckets = new Map<string, DayBucket>();

    for (const entry of logs) {
      const key = this.dayKey(entry.timestamp);
      const existing = buckets.get(key);

      if (existing) {
        existing.count++;
      } else {
        buckets.set(key, {
          key,
          title: this.formatDate(entry.timestamp),
          date: this.startOfDay(entry.timestamp),
          count: 1,
        });
      }
    }

    return [...buckets.values()].sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );
  }

  private renderTimeline(): void {
    if (!this.chart) return;

    const option: echarts.EChartsOption = {
      animation: false,
      grid: { left: 24, right: 24, top: 16, bottom: 16 },
      tooltip: {
        trigger: "item",
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          const bucket = this.dayBuckets[params.dataIndex];
          return bucket ? `${bucket.title}<br/>${bucket.count} log(s)` : "";
        },
      },
      xAxis: {
        type: "value",
        min: 0,
        max: this.dayBuckets.length + 1,
        axisLine: {
          show: true,
          onZero: true,
          lineStyle: { color: PRIMARY_COLOR, width: 2 },
        },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
      },
      yAxis: { type: "value", min: -1, max: 1, show: false },
      series: [
        {
          type: "scatter",
          symbol: "circle",
          symbolSize: 16,
          cursor: "pointer",
          data: this.dayBuckets.map((bucket, index) => ({
            value: [index + 1, 0],
            itemStyle: {
              color:
                bucket.key === this.selectedDayKey ? PRIMARY_COLOR : "#fff",
              borderColor: PRIMARY_COLOR,
              borderWidth: 2,
              opacity: 1,
            },
          })),
        },
      ],
    };

    this.chart.setOption(option);
  }

  private dayKey(date: Date): string {
    return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(
      date.getDate()
    )}`;
  }

  private formatDate(date: Date): string {
    return `${date.getDate()} ${
      OrderLogsModalComponent.MONTHS[date.getMonth()]
    } ${date.getFullYear()}`;
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private pad(value: number): string {
    return String(value).padStart(2, "0");
  }

  ngOnDestroy(): void {
    this.chart?.dispose();
    this.resizeObserver.disconnect();
  }
}
